import * as React from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import Chip from '@mui/material/Chip';
import moment from 'moment';

import { SliderWrapper } from '@components';
import { ListHomeEventsQuery, ListHomeSpotsQuery } from '@graphql';
import { getTrueKeys, truncateText, getSpotOpenStatus } from '@utils';
import { Slide, SlideProps } from './Slide';

interface SliderProps {
  category: ListHomeEventsQuery['eventCategories'][0] | ListHomeSpotsQuery['homeSpots'][0];
}

interface OpenChipProps {
  opening: ListHomeSpotsQuery['homeSpots'][0]['spots'][0]['opening'];
  closing: ListHomeSpotsQuery['homeSpots'][0]['spots'][0]['closing'];
}

interface OpenTextProps {
  opening: ListHomeSpotsQuery['homeSpots'][0]['spots'][0]['opening'];
  closing: ListHomeSpotsQuery['homeSpots'][0]['spots'][0]['closing'];
}

const Slider = (props: SliderProps) => {
  const { category } = props;
  const [slides, setSlides] = React.useState<SlideProps[]>([]);
  const { t } = useTranslation('common');

  React.useEffect(() => {
    if (category.__typename === 'HomeSpots') {
      const items = category.spots.map((spot) => {
        return {
          id: spot.id,
          type: 'Spot' as 'Spot',
          to: `?s=${spot.slug}`,
          title: spot.name,
          covers: spot.covers,
          defaultImage: spot.configuration.primaryType ? spot.configuration.primaryType + '.jpg' : null,
          subtitleLeft: [
            spot.configuration.primaryType,
            spot.configuration.secondaryType,
            spot.configuration.tertiaryType,
          ]
            .filter(Boolean)
            .map((type) => t(`types.${type}.label`, { ns: 'spot' }))
            .join(' • '),
          subtitleRight: spot.address?.city ?? t('word.unknown city'),
          textBottomLeft: getTrueKeys(spot.atmosphere)
            .map((key) => t(`atmosphere.${key}`, { ns: 'spot' }))
            .join(', '),
          textBottomRight: spot.configuration.pricing
            ? '€ ' + t(`pricing.${spot.configuration.pricing}`, { ns: 'spot' })
            : '€ ' + t(`word.${null}`, { ns: 'common' }),
          subTextBottomLeft: 'subTextBottomLeft',
          subTextBottomRight: 'subTextBottomRight',
          SubBottomLeft: <OpenChip opening={spot.opening} closing={spot.closing} />,
          SubBottomRight: <OpenText opening={spot.opening} closing={spot.closing} />,
        };
      });
      setSlides(items);
    } else if (category.__typename === 'EventCategory') {
      const items = category.events.map((event) => ({
        id: event.id,
        type: 'Event' as 'Event',
        to: `?e=${event.id}`,
        covers: event.covers,
        title: event.title,
        subtitleLeft: moment(event.from).format('dd DD MMM'),
        subtitleRight: moment(event.from).format('[à] HH[h]mm'),
        textBottomLeft: event.spot?.name || '',
        textBottomRight: '',
        subTextBottomLeft: t('category.' + event.category?.name, { ns: 'event' }),
        subTextBottomRight: event.spot?.address?.city || event.address?.city || t('word.unknown city'),
      }));
      setSlides(items);
    }
  }, [category]);

  return (
    <Stack width="100%" justifyContent="space-between" spacing={1}>
      <Typography
        pl={{ xs: 2, sm: 3, md: 10 }}
        variant="h5"
        fontWeight={'bold'}
        fontSize={{ xs: '1rem', sm: '1.1rem', md: '1.6rem' }}
      >
        {category.__typename === 'HomeSpots' && category.title}
        {category.__typename === 'EventCategory' && t('category.' + category.name, { ns: 'event' })}
      </Typography>
      <SliderWrapper>
        {slides.map((slide) => (
          <Slide key={slide.id} {...slide} />
        ))}
      </SliderWrapper>
    </Stack>
  );
};

export const OpenChip = (props: OpenChipProps) => {
  const { opening, closing } = props;
  const { t } = useTranslation('spot');

  const status = getSpotOpenStatus(opening, closing, t);

  return status.isOpen !== null ? (
    <Chip size="small" label={status.label} variant={status.variant} color={status.color} />
  ) : (
    <Typography variant="body2" fontSize=".8rem">
      {truncateText('', 35)}
    </Typography>
  );
};

export const OpenText = (props: OpenTextProps) => {
  const { opening, closing } = props;
  const { t } = useTranslation('spot');

  const status = getSpotOpenStatus(opening, closing, t);

  return status.isOpen !== null ? (
    <Typography variant="body2" fontSize=".8rem">
      {truncateText(status.detail, 35)}
    </Typography>
  ) : (
    <></>
  );
};

export default Slider;
